import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'

// Em dev, usa path relativo para o proxy do Vite funcionar (inclusive de outros dispositivos na rede)
// Em produção, usa VITE_API_URL ou path relativo (Nginx faz proxy de /api/)
const API_BASE_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '')

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor for auth tokens
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Only handle 401 errors from our API, not from browser extensions or other sources
    const isApiRequest = error.config?.baseURL === API_BASE_URL || error.config?.url?.startsWith('/api')

    // O login pela Cloudflare Access é uma tentativa silenciosa: a falha é tratada por quem chamou.
    if (originalRequest?.url?.includes('/auth/cloudflare')) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && isApiRequest) {
      // Se é o endpoint de refresh que falhou, não tentar novamente
      if (originalRequest.url?.includes('/auth/refresh')) {
        console.log('🔒 Refresh token expired - logging out')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
        return Promise.reject(error)
      }

      // Se já tentamos renovar este request, não tentar novamente
      if (originalRequest._retry) {
        console.log('🔒 Token refresh retry failed - logging out')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
        return Promise.reject(error)
      }

      originalRequest._retry = true

      // Se já está renovando, colocar request na fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            // Atualizar token no header e retentar
            const token = localStorage.getItem('accessToken')
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return apiClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      isRefreshing = true

      const refreshToken = localStorage.getItem('refreshToken')

      if (!refreshToken) {
        console.log('🔒 No refresh token - logging out')
        isRefreshing = false
        localStorage.removeItem('accessToken')
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
        return Promise.reject(error)
      }

      try {
        console.log('🔄 Access token expired - refreshing...')

        // Chamar endpoint de refresh
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        )

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data.tokens

        // Salvar novos tokens
        localStorage.setItem('accessToken', newAccessToken)
        localStorage.setItem('refreshToken', newRefreshToken)

        console.log('✅ Token refreshed successfully')

        // Atualizar header da requisição original
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        // Processar fila de requests que estavam esperando
        processQueue(null, newAccessToken)

        // Retentar requisição original
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('❌ Failed to refresh token:', refreshError)

        // Processar fila com erro
        processQueue(new Error('Token refresh failed'), null)

        // Limpar tokens e deslogar
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))

        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean
  data: T
  message?: string
  timestamp: string
}

export interface ApiError {
  success: false
  error: {
    message: string
    statusCode: number
    stack?: string
  }
  timestamp: string
  path: string
}